import { toFullName } from '@repo/toolkit/to-full-name'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { DeleteIcon, PenIcon, PhoneIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { OlContactInput, OlInput } from '../../../../lib/types/models/ol/ol-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import { generateColumns } from '../../../../utils/functions/generate-columns'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import OlContactForm from './ol-contact-form'

const olContactsColumns = generateColumns<OlContactInput>({
  order: ['lastName', 'firstName', 'email', 'phone', 'position'],
  headers: {
    lastName: 'NOM',
    firstName: 'Prénom',
    email: 'Email',
    phone: 'Téléphone',
    position: 'Poste'
  },
  sortableFields: ['lastName', 'firstName', 'email', 'position'],
  customCells: {
    phone: (row) => formatPhoneNumber(row.phone),
    lastName: (row) => row.lastName.toUpperCase()
  }
})

const OlContactsOlForm = () => {
  const form = useFormContext<OlInput>()
  const olContacts = form.watch('olContacts') || []

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    OlContactInput
  >(['create', 'edit', 'delete'])

  return (
    <ActionTableWrapper
      title={'Autres contacts prestataire'}
      actions={[
        <Button
          key={'button'}
          type="button"
          icon={PlusIcon}
          label={'Ajouter un contact'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />
      ]}
    >
      <DataTable<IdInput<OlContactInput>>
        manualSorting={false}
        columns={[
          ...olContactsColumns,
          {
            enableSorting: false,
            header: '',
            accessorKey: 'id',
            cell: ({ row }) => (
              <div className={'flex gap-2'}>
                <ActionButton
                  variant={'grey'}
                  icon={PenIcon}
                  onClick={() => {
                    toggleModal('edit', row.original)
                  }}
                />

                <ActionButton
                  variant={'danger'}
                  icon={DeleteIcon}
                  onClick={() => toggleModal('delete', row.original)}
                />
              </div>
            )
          }
        ]}
        display={olContacts.length > 0}
        data={olContacts.map((contact, index) => ({ ...contact, id: index }))}
        rowCount={olContacts.length}
        paginated={false}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un contact'}
        icon={PhoneIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <OlContactForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create'),
            onConfirm: (data) => {
              form.setValue('olContacts', [...olContacts, data])
              toggleModal('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <>
          <Modal
            isOpen={modalState.edit}
            title={'Éditer un contact'}
            icon={PhoneIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('edit')}
          >
            <OlContactForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={selectedData}
              buttonsConfig={modalButtonConfig({
                onCancel: () => toggleModal('edit'),
                onConfirm: (data) => {
                  form.setValue(
                    'olContacts',
                    olContacts.map((contact, index) =>
                      index === selectedData.id ? data : contact
                    )
                  )
                  toggleModal('edit')
                }
              })}
            />
          </Modal>

          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: () => {
                form.setValue(
                  'olContacts',
                  olContacts.filter((_, index) => index !== selectedData.id)
                )
                toggleModal('delete')
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={`Supprimer le contact "${toFullName(selectedData.firstName, selectedData.lastName)}" ?`}
            icon={DeleteIcon}
            variant={'destructive'}
          />
        </>
      )}
    </ActionTableWrapper>
  )
}

export default OlContactsOlForm
